import * as utils from '../utils/utils.js';
import * as userPerms from '../utils/userPerms.js';
import { VIEWING_STATE } from '../events/events.config.js';
import { add, addMinutes } from 'date-fns';
import { TableClient, AzureSASCredential } from '@azure/data-tables';

export default class Watch {
    constructor($scope, $interval, $sce, watchService) {
        this.$scope = $scope;
        this.$interval = $interval;
        this.$sce = $sce;
        this.watchService = watchService;
        this.possibleAccessSwitch = false;
        this.feedError = false;
        this.siteSettings = siteSettings;
        this.showViewCount = false;

        this.channelFeedEvents = [];
        this.recommendedEvents = [];

        this.imageVer = siteSettings.imageVersion;
        this.reloadThresholdDate = null;

        this.themeName = 'video-js ' + utils.getVideoJSThemeName() + ' vjs-16-9';

        this.noOfOnlineViewers = 0;
        this.watchService.liveStats = null;

        this.enableStatistics = siteSettings.statisticsSettings.filter((item) => item.key === 'EnableStatistics')[0]?.value === 'True';
        const onlineViewersStorageHostName = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'OnlineViewersStorageHostName'
        )[0]?.value;
        const sas = siteSettings.statisticsSettings.filter((item) => item.key === 'SAS')[0]?.value;
        const onlineViewersTableName = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'OnlineViewersTableName'
        )[0]?.value;
        this.onlineViewersOffsetMinutes = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'ShowOnlineViewersBeforeOffsetMinutes'
        )[0]?.value;
        this.onlineViewersTableClient = new TableClient(
            onlineViewersStorageHostName,
            onlineViewersTableName,
            new AzureSASCredential(sas)
        );
    }

    $onInit() {
        if (
            pageContext.accessKey === null ||
            pageContext.accessType === null ||
            pageContext.objectId === null
        )
            return;

        // Get the tokens from the URL hash and save them in localstorage
        const tokenMatch = window.location.hash.match(/u=(.+)&t=(.+)/i);
        if (tokenMatch) {
            const user = decodeURIComponent(tokenMatch[1]);
            localStorage.setItem('VCL.User', user);
            if (!this.watchService.user)
                this.watchService.user = user;
            localStorage.setItem('VCL.EmailAuthenticated-' + pageContext.accessKey, decodeURIComponent(tokenMatch[2]));
            this.removeHash();
        }

        this.$interval(() => {
            Promise.resolve(this.getOnlineViewers());
        }, 15000);

        // Configure Application Insights
        if (
            window.appInsights !== undefined &&
            window.appInsights !== null &&
            userPerms.aadAuthenticated(this.watchService.user)
        ) {
            window.appInsights.setAuthenticatedUserContext(this.watchService.user._ij);
        }

        if (siteSettings.isIncompatibleBrowser) {
            window.location.href = '/browser';
        } else {
            // Verify the user's credentials, redirect to sign in if needed, or fetch metadata if the user is completely signed in.
            // Don't verify PIN login for internal/fedauth webcasts at this stage, we'll do that after we have done the initial metadata fetch.
            // This is because we don't want admins to get the PIN login prompt.
            let signInRedirectUrl = userPerms.getSignInUrlIfUserNotAuthenticated(
                this.watchService.user,
                pageContext.accessType,
                !pageContext.accessType.requireFedAuth,
                null,
                false
            );
            if (signInRedirectUrl) {
                window.location.href = signInRedirectUrl;
            } else {
                // Initial fetch of webcast metadata
                Promise.resolve(
                    this.fetchPageContext(
                        pageContext.objectId,
                        pageContext.accessType.requireFedAuth
                    ).then(() => {
                        return this.getOnlineViewers();
                    })
                );

                // Fetch webcast metadata continuously with an interval
                this.$interval(() => {
                    // Perform a reload to update cached resources if neccessary.
                    if (
                        this.reloadThresholdDate !== null &&
                        this.reloadThresholdDate < new Date()
                    ) {
                        window.location.reload();
                    } else {
                        Promise.resolve(
                            this.fetchPageContext(
                                this.watchService.event.id,
                                this.watchService.event.eventAccessType.requireFedAuth
                            )
                        );
                    }
                }, siteSettings.webcastMetadataInterval);
            }
        }
    }

    removeHash() {
        history.pushState("", document.title, window.location.pathname
                                                           + window.location.search);
    }

    async getOnlineViewers() {
        if (
            !this.enableStatistics ||
            !this.watchService.event ||
            !this.showViewCount ||
            this.watchService.event.viewingState > 1
        )
            return;

        // Don't fetch online viewers if it's too long before the webcast start time
        if (
            new Date() <
            addMinutes(
                utils.getDateFromUTCString(this.watchService.event.startTime),
                this.onlineViewersOffsetMinutes
            )
        ) {
            this.noOfOnlineViewers = 0;
            return;
        }

        try {
            const entity = await this.onlineViewersTableClient.getEntity(
                this.watchService.event.accessKey,
                'LiveStatsMinimal'
            );
            if (entity && entity.Statistics !== undefined && entity.Statistics !== null) {
                this.watchService.liveStats = JSON.parse(entity.Statistics);
                if (
                    this.watchService.liveStats.OnlineViewers !== undefined &&
                    this.watchService.liveStats.OnlineViewers !== null &&
                    this.watchService.liveStats.OnlineViewers >= 0
                ) {
                    this.noOfOnlineViewers = this.watchService.liveStats.OnlineViewers;
                }
            } else {
                utils.log(
                    'statistics',
                    "[COUNTDOWN] Online viewers entity doesn't match the required criteria",
                    true,
                    1
                );
            }
        } catch (error) {
            utils.log('statistics', JSON.stringify(error), true, 1);
        }
        return;
    }

    fetchPageContext(id, isInternal) {
        let apiCallType = isInternal ? 'int/' : '';
        let reqUrl = `/events/pagecontext/${apiCallType}watch/${id}`;
        let ctrl = this;

        return fetch(reqUrl, {
            credentials: 'omit',
        })
            .then((response) => {
                utils.setUserLocation(response.headers);

                if (response.status === 400 && !ctrl.possibleAccessSwitch) {
                    // 400 could mean that the webcast has changed access type,
                    // so we should try the different route only once
                    ctrl.possibleAccessSwitch = true;
                    return ctrl.fetchPageContext(id, !isInternal);
                } else if (response.status === 404) {
                    // Event does not exist/is deleted - redirect to error page
                    window.location.href = `/Home/Error?statusCode=404&embed=${siteSettings.isEmbeddedPage}`;
                    return Promise.resolve();
                } else if (response.status === 200) {
                    return response.json();
                }
            })
            .then((data) => {
                if (!data || data.length === 0 || !data.event) {
                    utils.log(
                        'watch - webcast data',
                        'Error fetching webcast data, response was empty',
                        false,
                        3
                    );
                    return Promise.reject();
                }

                // Reset the access switch variable upon successful fetch
                ctrl.possibleAccessSwitch = false;

                // Verify the user's credentials in every interval, as the webcast settings might have changed or the user's cookies have expired
                let signInRedirectUrl = userPerms.getSignInUrlIfUserNotAuthenticated(
                    ctrl.watchService.user,
                    data.event.eventAccessType,
                    true,
                    data.eAs,
                    data.event.signInRequiredForExternalUsers
                );

                if (signInRedirectUrl) {
                    window.location.href = signInRedirectUrl;
                    return Promise.resolve();
                } else {
                    // No sign in needed for this user, manage page updates of webcast data
                    ctrl.manageUpdates(data);
                    ctrl.$scope.$apply();
                    return Promise.resolve();
                }
            });
    }

    manageUpdates(newEventData) {
        if (!this.imgUrls) {
            // Preload images that are used in the video player
            this.imgUrls = [
                newEventData.event.mediaUrls['VideoPoster'],
                siteSettings.waitingLivePictureUrl,
                siteSettings.waitingVoDPictureUrl,
                siteSettings.waitingEmbedPictureUrl,
            ];
            for (var i = 0; i < this.imgUrls.length; i++) {
                var img = new Image();
                img.src = this.imgUrls[i];
            }
        }

        // If certain properties have changed, reload the page
        if (
            this.watchService.event !== null &&
            this.watchService.event.postEventBehavior !== newEventData.event.postEventBehavior
        ) {
            window.location.reload();
        }

        // If we are not already awaiting a scheduled page reload
        // check for version updates and if the version is changed,
        // schedule a reload after the page cache has expired.
        if (this.reloadThresholdDate === null) {
            let currentAppVersionCachedResources = siteSettings.appVersion;

            if (parseInt(newEventData.event.appVersion) !== currentAppVersionCachedResources) {
                this.reloadThresholdDate = add(new Date(), {
                    minutes: siteSettings.versionReloadOffsetMinutes,
                });
            }
        }

        let previousEventSettings = this.watchService.event;

        this.updateVideoService(newEventData);

        this.determineCorrectView(previousEventSettings, newEventData.event);

        this.setCalculatedValues(newEventData);

        this.channelFeedEvents = newEventData.channelEvents;
        this.recommendedEvents = newEventData.recommended;
        this.showViewCount =
            this.enableStatistics &&
            (!newEventData.event.hideViewCount ||
            (newEventData.event.hideViewCount &&
                userPerms.hasElevatedEventPermissions(this.watchService.user, newEventData.eAs)));
    }

    getChannelTitle(event) {
        if (event.channel === null) return '';

        return event.channel.title;
    }

    getChannelPath(event) {
        if (event.channel === null) return '';

        return event.channel.directUrl;
    }

    isLive(event) {
        event.viewingState === VIEWING_STATE.live;
    }

    getLocalDate(event) {
        if (event === undefined || event === null) return '';
        return utils.parseDateToLocale(event.startTime);
    }

    setCalculatedValues(newPageContext) {
        let ld = newPageContext.event.lobbyDescriptionAsHTML;
        if (ld === null || ld.length === 0) ld = newPageContext.event.descriptionAsHTML;

        this.lobbyDescription = this.$sce.trustAsHtml(ld);
        this.watchDescription = this.$sce.trustAsHtml(newPageContext.event.descriptionAsHTML);
        this.videoEmbedCode = this.$sce.trustAsHtml(newPageContext.event.videoEmbedCode);
        this.wowzaVideoEmbedCode = this.$sce.trustAsHtml(newPageContext.event.wowzaVideoEmbedCode);

        this.posterUrl = newPageContext.event.mediaUrls['VideoPoster'];
        this.embedBackground = newPageContext.event.mediaUrls['Background'];

        // Title tag
        // Assume that the title contains a pipe '|' char
        document.title = document.title.replace(
            /^(.+)(\|.+)$/,
            newPageContext.event.titleCombined + ' $2'
        );
    }

    updateVideoService(newPageContext) {
        this.watchService.voDStreamUrl = newPageContext.voDStreamUrl;
        this.watchService.voDStreamUrlChina = newPageContext.voDStreamUrlChina;
        this.watchService.cloudStreamUrl = newPageContext.cloudStreamUrl;
        this.watchService.cloudStreamBackupUrl = newPageContext.cloudStreamBackupUrl;
        this.watchService.chinaCdnStreamUrl = newPageContext.chinaCdnStreamUrl;
        this.watchService.onPremStreamUrls = newPageContext.onPremStreamUrls;
        this.watchService.onPremStreamSources = newPageContext.onPremStreamSources;
        this.watchService.onPremVodUrls = newPageContext.onPremVodUrls;
        this.watchService.eAdms = newPageContext.eAs;

        this.watchService.event = newPageContext.event;
    }

    determineCorrectView(oldEventData, newEventData) {
        if (newEventData === null || newEventData === undefined) return;

        let currentView = this.watchService.currentView;
        let mayUserSwitchViews = userPerms.hasElevatedEventPermissions(
            this.watchService.user,
            this.watchService.eAdms
        );

        if (currentView === this.watchService.CURRENT_VIEW.undef) {
            // Initial views
            let selectedAdminViewFromQueryString = null;
            if (mayUserSwitchViews) {
                // Check to see if the user is allowed to swithc views and if they have selected a certain view
                const urlParams = new URLSearchParams(window.location.search);
                selectedAdminViewFromQueryString = urlParams.get('view');
            }

            if (
                (utils.isEventInFuture(newEventData) &&
                    selectedAdminViewFromQueryString !== 'webcast') ||
                selectedAdminViewFromQueryString === 'countdown'
            ) {
                // Initial setup for countdown
                this.watchService.currentView = this.watchService.CURRENT_VIEW.countdown;
            } else if (
                (utils.hasEventStarted(newEventData) &&
                    selectedAdminViewFromQueryString !== 'countdown') ||
                selectedAdminViewFromQueryString === 'webcast' ||
                (mayUserSwitchViews && newEventData.viewingState === VIEWING_STATE.ended)
            ) {
                // Initial setup for video
                this.watchService.currentView = this.watchService.CURRENT_VIEW.video;
                if (!this.useVideoEmbed()) this.watchService.prepareVideoView();
            } else {
                this.watchService.currentView = this.watchService.CURRENT_VIEW.ended;
            }
        } else if (oldEventData !== null) {
            // Do we have a view transition?
            if (utils.hasEventStarted(oldEventData) && utils.isEventInFuture(newEventData)) {
                // If webcast was postponed, switch back to countdown view
                this.watchService.currentView = this.watchService.CURRENT_VIEW.countdown;
            } else if (
                utils.hasEventGracePeriodEnded(newEventData) &&
                newEventData.viewingState === VIEWING_STATE.ended
            ) {
                // Event has ended && we are not using VOD
                // Show the ended view or do a redirect
                if (newEventData.postEventBehavior === 2 && newEventData.redirectUrl) {
                    window.location.href = this.watchService.event.redirectUrl;
                } else if (!mayUserSwitchViews) {
                    this.watchService.currentView = this.watchService.CURRENT_VIEW.ended;
                }
            } else if (this.watchService.currentView === this.watchService.CURRENT_VIEW.video) {
              // If nothing has changed, toggle the video player
              this.watchService.toggleVideoPlayer();
            }
        }
    }

    useVideoEmbed() {
        return (
            this.watchService.event &&
            this.watchService.event.videoEmbedCode &&
            this.watchService.event.videoEmbedCode.length > 0
        );
    }
}
